import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Download,
  ExternalLink,
  Mail,
  Home,
  User,
  Briefcase,
  Award,
  MapPin,
  Calendar,
  Globe,
  Zap,
  Target,
  GraduationCap,
  CheckCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';
import { GitHubIcon, LinkedInIcon, XIcon } from '../components/SocialIcons';
import SEO from '../components/SEO';
import { useAnalytics } from '../components/Analytics';
import { contentData } from '../utils/contentManager';

// Shared button styles for consistent CTAs across the page
const btnPrimary =
  'inline-flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-gray-900 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold transition-all text-sm sm:text-base shadow-sm';
const btnSecondary =
  'inline-flex items-center justify-center space-x-2 border border-gray-500/70 text-gray-200 hover:border-cyan-400/60 hover:text-cyan-300 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold transition-all text-sm sm:text-base';
const btnPager =
  'inline-flex items-center justify-center space-x-1 sm:space-x-2 border border-white/15 text-gray-300 hover:border-cyan-400/50 hover:text-cyan-300 px-3 sm:px-4 py-2 rounded-full transition-all text-sm sm:text-base bg-black/20';
const btnPagerDisabled =
  'inline-flex items-center justify-center space-x-1 sm:space-x-2 border border-white/10 text-gray-600 px-3 sm:px-4 py-2 rounded-full text-sm sm:text-base cursor-not-allowed bg-black/10';

// Shared surfaces — match About focus panel / tech-grid theme
const surfaceCard =
  'rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm';
const surfaceCardHover =
  'rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm hover:border-cyan-400/30 transition-colors duration-300';
const surfaceTag =
  'px-2.5 py-1 rounded-md border border-white/10 bg-black/25 text-xs text-gray-300 font-medium';
const surfaceTagSoft =
  'px-2 sm:px-3 py-1 rounded-lg border border-white/10 bg-black/25 text-xs sm:text-sm text-gray-300 font-medium hover:border-cyan-500/30 hover:text-cyan-300 transition-all';
const surfaceChip =
  'px-3 py-1.5 rounded-full border border-white/10 bg-black/25 text-xs text-gray-300';
const surfacePill =
  'inline-block px-3 py-1.5 rounded-lg border border-white/10 bg-black/25 text-xs sm:text-sm text-gray-200';
const surfaceInput =
  'w-full px-3 sm:px-4 py-3 sm:py-4 bg-black/25 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 focus:outline-none transition-all text-base disabled:opacity-50 disabled:cursor-not-allowed';
const surfaceEyebrow =
  'inline-block mb-4 px-3 py-1 text-xs uppercase tracking-widest text-gray-400 border border-white/10 rounded-full bg-black/20';
const surfaceIconBtn =
  'w-10 h-10 sm:w-12 sm:h-12 border border-white/10 bg-black/25 rounded-xl flex items-center justify-center text-gray-300 hover:text-cyan-300 hover:border-cyan-400/40 transition-all';

// Shared page shell — balanced professional width, consistent edges
const pagePad = 'px-4 sm:px-6 lg:px-8';
const pageWidth = 'max-w-7xl mx-auto w-full';

// Rotating role titles for the typing animation (module-level so the array reference is stable across renders)
const roles = [
  'Technical Analyst',
  'Developer',
  'Problem Solver'
];

const TechPage = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(false);
  const floatingNavHoveredRef = useRef(false);
  const [currentCertPage, setCurrentCertPage] = useState(0);
  const [currentProjectPage, setCurrentProjectPage] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    errorMessage: ''
  });
  
  const analytics = useAnalytics();

  // Get data from content manager
  const projects = contentData.featured_projects.filter((project) => project.featured);
  const skills = contentData.skills;
  const experience = contentData.experience;
  const workExperience = experience.filter(exp => exp.type === 'work');
  const education = experience.filter(exp => exp.type === 'education');
  const certificates = contentData.certifications;
  const personalInfo = contentData.personal;
  const socialLinks = contentData.social;
  const projectsPerPage = 4;
  const totalProjectPages = Math.ceil(projects.length / projectsPerPage);
  const paginatedProjects = projects.slice(
    currentProjectPage * projectsPerPage,
    (currentProjectPage + 1) * projectsPerPage
  );

  // In-page navigation items
  const sectionNavItems = [
    { id: 'about', label: 'About', icon: User },
    { id: 'experience', label: 'Experience', icon: Briefcase },
    { id: 'projects', label: 'Projects', icon: Target },
    { id: 'skills', label: 'Skills', icon: Zap },
    { id: 'certificates', label: 'Certificates', icon: Award },
    { id: 'contact', label: 'Contact', icon: Mail }
  ];

  const mobileMenuItems = [
    { id: 'hero', label: 'Home', icon: Home },
    ...sectionNavItems
  ];

  // Form handlers
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Reset status and start submitting
    setFormStatus({
      isSubmitting: true,
      isSuccess: false,
      isError: false,
      errorMessage: ''
    });

    try {
      // Send to your Vercel API endpoint with pageType
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          pageType: 'tech' // This will route to himankarora1000@gmail.com
        }),
      });

      // Check if response is ok
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if response has content
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const result = await response.json();

      if (result.success) {
        // Track successful form submission
        if (analytics?.trackPortfolioEvents) {
          analytics.trackPortfolioEvents.contactForm('tech-contact-form-success');
        }
        
        // Show success message and clear form
        setFormStatus({
          isSubmitting: false,
          isSuccess: true,
          isError: false,
          errorMessage: ''
        });
        
        setFormData({ name: '', email: '', subject: '', message: '' });
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setFormStatus({
            isSubmitting: false,
            isSuccess: false,
            isError: false,
            errorMessage: ''
          });
        }, 5000);
        
      } else {
        throw new Error(result.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Email sending error:', error);
      
      // Track failed form submission
      if (analytics?.trackPortfolioEvents) {
        analytics.trackPortfolioEvents.contactForm('tech-contact-form-error');
      }
      
      // Show specific error message
      let errorMessage = 'Failed to send message. ';
      
      if (error.message.includes('HTTP error! status: 405')) {
        errorMessage += 'API endpoint not configured properly.';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage += 'Network error. Please check your connection.';
      } else if (error.message.includes('non-JSON response')) {
        errorMessage += 'Server configuration error.';
      } else {
        errorMessage += `Please try again or email me directly at ${personalInfo.email}`;
      }
      
      setFormStatus({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage: errorMessage
      });
    }
  };

  // Event handlers with analytics
  const handleResumeDownload = () => {
    if (analytics?.trackPortfolioEvents) {
      analytics.trackPortfolioEvents.resumeDownload();
    }
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    if (analytics?.trackPortfolioEvents) {
      analytics.trackPortfolioEvents.projectView(project.id, project.title);
    }
  };

  const closeProjectDetail = () => setSelectedProject(null);

  const handleProjectDemo = (project) => {
    if (analytics?.trackPortfolioEvents) {
      analytics.trackPortfolioEvents.projectDemo(project.id, project.demo);
    }
  };

  const handleProjectGithub = (project) => {
    if (analytics?.trackPortfolioEvents) {
      analytics.trackPortfolioEvents.projectGithub(project.id, project.github);
    }
  };

  const handleSocialClick = (platform, url) => {
    if (analytics?.trackPortfolioEvents) {
      analytics.trackPortfolioEvents.socialClick(platform, url);
    }
  };

  // Close project detail on Escape
  useEffect(() => {
    if (!selectedProject) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeProjectDetail();
    };
    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [selectedProject]);

  // Fixed typing animation effect
  useEffect(() => {
    const currentRole = roles[currentRoleIndex];
    
    const typeChar = () => {
      if (isTyping && !isDeleting) {
        if (displayText.length < currentRole.length) {
          setDisplayText(currentRole.slice(0, displayText.length + 1));
        } else {
          // Finished typing, wait then start deleting
          setTimeout(() => {
            setIsDeleting(true);
          }, 2000);
        }
      } else if (isDeleting) {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          // Finished deleting, move to next role
          setIsDeleting(false);
          setCurrentRoleIndex((prev) => (prev + 1) % roles.length);
        }
      }
    };

    const speed = isDeleting ? 50 : 100; // Faster deletion
    const timer = setTimeout(typeChar, speed);

    return () => clearTimeout(timer);
  }, [displayText, isTyping, isDeleting, currentRoleIndex]);

  // Scroll detection — floating nav shows while scrolling, hides after idle
  useEffect(() => {
    let hideTimer = null;

    const handleScroll = () => {
      const sections = ['hero', 'about', 'experience', 'projects', 'skills', 'certificates', 'contact'];
      const scrollPosition = window.scrollY + 200;
      const isScrolled = window.scrollY > 120;

      // Calculate scroll progress
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const totalScrollableHeight = documentHeight - windowHeight;
      const currentScrollProgress = Math.min((window.scrollY / totalScrollableHeight) * 100, 100);
      setScrollProgress(currentScrollProgress);

      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }

      if (isScrolled) {
        setShowFloatingNav(true);
        hideTimer = setTimeout(() => {
          if (!floatingNavHoveredRef.current) {
            setShowFloatingNav(false);
          }
        }, 1400);
      } else {
        setShowFloatingNav(false);
      }

      let currentSection = 'hero';

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop } = element;
          if (scrollPosition >= offsetTop) {
            currentSection = section;
            break;
          }
        }
      }

      setActiveSection(currentSection);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    setTimeout(handleScroll, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false); // Close mobile menu after selection
    
    // Small delay to ensure menu closes before scrolling
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const offsetTop = element.offsetTop - 72; // Fixed nav height
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    }, 100);
    
    // Track section navigation
    if (analytics?.trackPortfolioEvents) {
      analytics.trackPortfolioEvents.sectionView(sectionId);
    }
  };

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const timer = setTimeout(() => scrollToSection(hash), 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount for deep links
  }, []);

  return (
    <>
      {/* SEO */}
      <SEO 
        title="Himank Arora - Technical Analyst & Developer"
        description="Turning business needs into working products across data tools, web apps, and AI-enabled systems."
        keywords="technical analyst, developer, business analysis, React, Python, data analysis, Boston"
      />

      {/* Professional Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-[#05070b]/70 backdrop-blur-md pt-[env(safe-area-inset-top)]">
        <div className={`${pageWidth} ${pagePad}`}>
          <div className="flex h-14 items-center justify-between sm:h-16">
            <Link 
              to="/tech" 
              className="group flex items-center space-x-2.5 transition-colors duration-300 sm:space-x-3"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-white/70 bg-transparent transition-colors group-hover:border-cyan-400 sm:h-10 sm:w-10">
                <span className="font-display text-xs font-bold tracking-tight text-white sm:text-sm">HA</span>
              </div>
              <span className="font-display text-base font-semibold tracking-tight text-white transition-colors group-hover:text-cyan-300 sm:text-lg">
                {personalInfo.name}
              </span>
            </Link>

            <div className="flex items-center space-x-3 sm:space-x-5">
              {/* Desktop in-page section links */}
              <div className="hidden items-center lg:flex">
                {sectionNavItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => scrollToSection(item.id)}
                    className={`relative px-3 py-2 text-sm font-medium transition-colors duration-300 ${
                      activeSection === item.id
                        ? 'text-white'
                        : 'text-gray-400 hover:text-cyan-300'
                    }`}
                  >
                    {item.label}
                    <span
                      className={`absolute left-3 right-3 -bottom-0.5 h-px rounded-full bg-cyan-400 transition-opacity duration-300 ${
                        activeSection === item.id ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  </button>
                ))}
              </div>

              <div className="hidden h-6 w-px bg-white/15 md:block" />

              {/* Portfolio Hub — Desktop */}
              <div className="hidden md:block">
                <Link
                  to="/"
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-400 transition-colors hover:text-cyan-300"
                >
                  <Globe size={15} />
                  <span>Portfolio Hub</span>
                </Link>
              </div>

              {/* Mobile Hamburger */}
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                className="rounded-full border border-white/10 bg-white/5 p-2.5 text-gray-300 transition-colors hover:border-cyan-400/40 hover:text-cyan-300 lg:hidden min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile / tablet dropdown (shown below lg where section links hide) */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="border-t border-white/10 bg-[#05070b]/95 backdrop-blur-md lg:hidden"
            >
              <div className="space-y-1 px-3 py-3">
                {mobileMenuItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => scrollToSection(item.id)}
                    className={`flex w-full items-center space-x-3 rounded-full px-4 py-3 text-left text-sm transition-colors ${
                      activeSection === item.id
                        ? 'bg-white/10 text-cyan-300'
                        : 'text-gray-300 hover:bg-white/5 hover:text-cyan-300'
                    }`}
                  >
                    <item.icon size={16} />
                    <span className="font-medium">{item.label}</span>
                  </button>
                ))}
                
                <div className="mt-2 border-t border-white/10 pt-2">
                  <Link
                    to="/"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex w-full items-center space-x-3 rounded-full px-4 py-3 text-sm text-gray-300 transition-colors hover:bg-white/5 hover:text-cyan-300"
                  >
                    <Globe size={16} />
                    <span className="font-medium">Portfolio Hub</span>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Scroll Progress Bar */}
      <div className="fixed top-14 left-0 right-0 z-40 h-px bg-white/5 sm:top-16">
        <motion.div
          className="h-full bg-cyan-400/80"
          style={{ width: `${scrollProgress}%` }}
          initial={{ width: 0 }}
          animate={{ width: `${scrollProgress}%` }}
          transition={{ duration: 0.1, ease: 'easeOut' }}
        />
      </div>

      {/* Floating Navigation — shows while scrolling, hides shortly after idle */}
      <AnimatePresence>
        {showFloatingNav && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="fixed top-[4.5rem] left-0 right-0 z-40 flex justify-center px-3 sm:top-24 sm:px-0"
            onMouseEnter={() => {
              floatingNavHoveredRef.current = true;
            }}
            onMouseLeave={() => {
              floatingNavHoveredRef.current = false;
              setShowFloatingNav(false);
            }}
          >
            <nav className="rounded-full border border-white/10 bg-black/55 px-2 py-1.5 shadow-lg shadow-black/20 backdrop-blur-md sm:px-3 sm:py-2">
              <div className="flex items-center space-x-0.5 sm:space-x-1">
                {sectionNavItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => scrollToSection(item.id)}
                    aria-label={item.label}
                    className={`rounded-full p-2 transition-colors duration-200 sm:p-2.5 ${
                      activeSection === item.id
                        ? 'bg-cyan-500/20 text-cyan-300'
                        : 'text-gray-400 hover:bg-white/5 hover:text-cyan-300'
                    }`}
                  >
                    <item.icon size={16} className="sm:w-[17px] sm:h-[17px]" />
                  </button>
                ))}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="min-h-screen tech-grid-bg relative overflow-hidden">
        {/* Hero Section */}
        <section 
          id="hero" 
          className={`relative z-10 flex min-h-screen items-center justify-center ${pagePad} pt-24 pb-12 sm:pt-28 sm:pb-16`}
        >
          <div className={pageWidth}>
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
              
              {/* Left side - Text Content */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                className="space-y-4 text-left sm:space-y-5"
              >
                <motion.p
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06, duration: 0.35 }}
                  className="text-[11px] sm:text-xs uppercase tracking-[0.22em] text-gray-500 font-medium"
                >
                  Technical Analyst & Developer
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.4 }}
                >
                  <p className="font-display text-lg sm:text-xl md:text-2xl text-gray-400 mb-2">
                    Hi, I'm
                  </p>
                  <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-white tracking-tight leading-[1.05]">
                    Himank{' '}
                    <span className="relative inline-block">
                      Arora
                      <span className="absolute left-0 -bottom-1 h-[3px] w-full rounded-full bg-cyan-400/80" />
                    </span>
                  </h1>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                  className="font-display text-xl sm:text-2xl md:text-3xl font-medium text-gray-300 min-h-[1.75rem] sm:min-h-[2.25rem]"
                >
                  <span className="text-cyan-400">
                    {displayText}
                    <span className="animate-pulse text-cyan-300/80">|</span>
                  </span>
                </motion.div>
                
                <motion.p 
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.28, duration: 0.4 }}
                  className="text-sm sm:text-base md:text-[1.05rem] text-gray-400 max-w-xl leading-relaxed"
                >
                  {personalInfo.bio}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.36, duration: 0.4 }}
                  className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4 pt-1"
                >
                  <button
                    onClick={() => scrollToSection('projects')}
                    className={btnPrimary}
                  >
                    <Target size={16} className="sm:w-5 sm:h-5" />
                    <span>View My Work</span>
                  </button>
                  
                  <a
                    href={personalInfo.resume}
                    download
                    onClick={handleResumeDownload}
                    className={btnSecondary}
                  >
                    <Download size={16} className="sm:w-5 sm:h-5" />
                    <span>Download Resume</span>
                  </a>

                  <button
                    onClick={() => scrollToSection('contact')}
                    className={btnSecondary}
                  >
                    <Mail size={16} className="sm:w-5 sm:h-5" />
                    <span>Get in Touch</span>
                  </button>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.44, duration: 0.4 }}
                  className="flex space-x-3 sm:space-x-4 pt-2"
                >
                  <a 
                    href={socialLinks.github} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => handleSocialClick('GitHub', socialLinks.github)}
                    className="text-gray-500 hover:text-cyan-400 transition-colors p-2"
                  >
                    <GitHubIcon size={22} className="sm:w-6 sm:h-6" />
                  </a>
                  <a 
                    href={socialLinks.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => handleSocialClick('LinkedIn', socialLinks.linkedin)}
                    className="text-gray-500 hover:text-cyan-400 transition-colors p-2"
                  >
                    <LinkedInIcon size={22} className="sm:w-6 sm:h-6" />
                  </a>
                  <a 
                    href={socialLinks.x_twitter} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    onClick={() => handleSocialClick('X Twitter', socialLinks.x_twitter)}
                    className="text-gray-500 hover:text-cyan-400 transition-colors p-2"
                  >
                    <XIcon size={22} className="sm:w-6 sm:h-6" />
                  </a>
                  <a 
                    href={`mailto:${personalInfo.email}`}
                    onClick={() => handleSocialClick('Email', personalInfo.email)}
                    className="text-gray-500 hover:text-cyan-400 transition-colors p-2"
                  >
                    <Mail size={22} className="sm:w-6 sm:h-6" />
                  </a>
                </motion.div>
              </motion.div>

              {/* Right side - Code editor animation */}
              <motion.div
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.45 }}
                className="relative mt-4 flex items-center justify-center lg:mt-0"
              >
                <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-md">
                  <div className={`${surfaceCard} p-4 sm:p-6`}>
                    <div className="flex items-center space-x-2 mb-3 sm:mb-4">
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full"></div>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-500 rounded-full"></div>
                      <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                    </div>
                    
                    <div className="space-y-2 sm:space-y-3 font-mono text-xs sm:text-sm">
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9, duration: 0.4 }}
                        className="text-blue-400"
                      >
                        <span className="text-cyan-400">const</span> professional = {'{'}
                      </motion.div>
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.15, duration: 0.4 }}
                        className="text-gray-300 ml-2 sm:ml-4"
                      >
                        name: <span className="text-green-400">'{personalInfo.name}'</span>,
                      </motion.div>
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.4, duration: 0.4 }}
                        className="text-gray-300 ml-2 sm:ml-4"
                      >
                        skills: [<span className="text-green-400">'Analysis'</span>, <span className="text-green-400">'React'</span>],
                      </motion.div>
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.65, duration: 0.4 }}
                        className="text-gray-300 ml-2 sm:ml-4"
                      >
                        focus: <span className="text-green-400">'Ship products'</span>
                      </motion.div>
                      <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.9, duration: 0.4 }}
                        className="text-blue-400"
                      >
                        {'};'}
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Signature skill marquee */}
        <div className="relative z-10 border-y border-white/10 bg-black/20 py-4 sm:py-5 overflow-hidden">
          <div className="tech-marquee-mask">
            <div className="flex w-max animate-marquee whitespace-nowrap">
              {[0, 1].map((copy) => (
                <div key={copy} className="flex items-center">
                  {['Requirements', 'Analysis', 'React', 'SQL', 'Systems', 'Delivery', 'Data', 'Product'].map((label, i) => (
                    <span key={`${copy}-${label}`} className="flex items-center">
                      <span className="font-display text-sm sm:text-base tracking-[0.12em] uppercase text-gray-400 px-4 sm:px-6">
                        {label}
                      </span>
                      <span className="text-cyan-400/70 text-xs" aria-hidden="true">✦</span>
                      {i === 7 && <span className="w-4 sm:w-6" />}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* About Me Section */}
        <section id="about" className={`flex min-h-screen items-center border-b border-white/5 py-14 sm:py-16 lg:py-20 ${pagePad}`}>
          <div className={pageWidth}>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              viewport={{ once: true, amount: 0.2 }}
              className="mb-10 text-center sm:mb-12"
            >
              <span className={surfaceEyebrow}>
                Background
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-3 sm:mb-4">
                About <span className="text-cyan-400">Me</span>
              </h2>
              <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
                Turning analysis into shipped tools and products.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-start">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                viewport={{ once: true, amount: 0.2 }}
              >
                <div className="space-y-4 sm:space-y-6 text-gray-300 text-base sm:text-lg leading-relaxed">
                  <p>
                    I'm a Technical Analyst and developer with 3+ years as the sole technical resource at growing businesses: owning requirements and analysis, then shipping the tools myself across data platforms, web products, and AI-enabled applications.
                  </p>
                  <p>
                    I work both sides of the problem: eliciting needs, mapping workflows, and validating impact with data, while also designing and building the systems that make those improvements real.
                  </p>
                  <p>
                    Whether it's a reporting dashboard, a product workflow redesign, or a full-stack application, I care about clear problem framing, measurable outcomes, and software that holds up in real use.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.08 }}
                viewport={{ once: true, amount: 0.2 }}
                className={`${surfaceCard} p-6 sm:p-8`}
              >
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-2">Currently</p>
                    <h3 className="font-display text-xl sm:text-2xl font-semibold text-white leading-snug">
                      Technical Analyst & Developer
                    </h3>
                  </div>
                  <div className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    <span className="text-[11px] text-emerald-300 font-medium">Open</span>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3 text-sm sm:text-base text-gray-300">
                    <MapPin size={15} className="text-cyan-400 shrink-0" />
                    <span>{personalInfo.location}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm sm:text-base text-gray-300">
                    <Clock size={15} className="text-cyan-400 shrink-0" />
                    <span>3+ years analyzing and shipping products</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm sm:text-base text-gray-300">
                    <GraduationCap size={15} className="text-cyan-400 shrink-0" />
                    <span>MS Information Systems, Northeastern ’26</span>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-3">Focus</p>
                  <div className="flex flex-wrap gap-2">
                    {['Analysis', 'React', 'SQL', 'Systems', 'Delivery'].map((chip) => (
                      <span
                        key={chip}
                        className={surfaceChip}
                      >
                        {chip}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Experience & Education Section */}
        <section id="experience" className={`relative scroll-mt-16 overflow-hidden border-b border-white/5 py-14 sm:py-16 lg:py-20 ${pagePad}`}>
          <div className={`relative ${pageWidth}`}>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              viewport={{ once: true, amount: 0.2 }}
              className="mb-10 text-center sm:mb-12"
            >
              <span className={surfaceEyebrow}>
                Career
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-3 sm:mb-4">
                Experience & <span className="text-cyan-400">Education</span>
              </h2>
              <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
                Roles, projects, and education that shaped how I work.
              </p>
            </motion.div>

            {/* Professional Experience — centered timeline */}
            <div className="mb-16 sm:mb-24">
              <div className="flex items-center justify-center gap-3 mb-10 sm:mb-14">
                <div className="w-11 h-11 rounded-xl border border-white/10 bg-black/25 flex items-center justify-center">
                  <Briefcase size={18} className="text-cyan-300" />
                </div>
                <div className="text-left">
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">Professional Experience</h3>
                  <p className="text-gray-500 text-xs sm:text-sm">Roles and impact across organizations</p>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-2 bottom-2 w-px bg-white/10 md:-translate-x-1/2" />

                <div className="space-y-12 sm:space-y-16">
                  {workExperience.map((exp, index) => (
                    <motion.div
                      key={exp.id || index}
                      initial={{ opacity: 0, y: 28 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      viewport={{ once: true, amount: 0.2 }}
                      className="relative grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10 lg:gap-14"
                    >
                      {/* Timeline node */}
                      <div
                        className={`absolute left-4 md:left-1/2 top-3 md:top-4 w-3 h-3 rounded-full -translate-x-1/2 z-10 ${
                          index === 0
                            ? 'bg-cyan-400 ring-[6px] ring-cyan-400/20 shadow-[0_0_18px_rgba(34,211,238,0.45)]'
                            : 'bg-white ring-[3px] ring-gray-800'
                        }`}
                      />

                      {/* Left: date, title, company */}
                      <div className="pl-10 md:pl-0 md:pr-8 lg:pr-12 md:text-right">
                        <span className={surfacePill}>
                          {exp.duration}
                        </span>
                        <h4 className="text-xl sm:text-2xl font-bold text-white leading-snug">
                          {exp.title}
                        </h4>
                        <p className="mt-1.5 text-cyan-400 font-medium text-sm sm:text-base">
                          {exp.company}
                        </p>
                        {exp.location && (
                          <p className="mt-1 text-gray-500 text-xs sm:text-sm flex items-center gap-1.5 md:justify-end">
                            <MapPin size={12} className="text-gray-500 shrink-0" />
                            {exp.location}
                          </p>
                        )}
                      </div>

                      {/* Right: description, achievements, skills */}
                      <div className="pl-10 md:pl-8 lg:pl-12">
                        {exp.description && (
                          <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-4">
                            {exp.description}
                          </p>
                        )}

                        {exp.achievements?.length > 0 && (
                          <ul className="space-y-2 mb-5">
                            {exp.achievements.map((achievement, i) => (
                              <li key={i} className="flex items-start gap-2.5 text-gray-400 text-sm leading-relaxed">
                                <span className="mt-2 h-1 w-1 rounded-full bg-cyan-400/80 shrink-0" />
                                <span>{achievement}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {exp.skills?.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {exp.skills.map((skill) => (
                              <span
                                key={skill}
                                className={surfaceTag}
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Education — same centered timeline */}
            <div id="education" className="scroll-mt-16">
              <div className="flex items-center justify-center gap-3 mb-10 sm:mb-14">
                <div className="w-11 h-11 rounded-xl border border-white/10 bg-black/25 flex items-center justify-center">
                  <GraduationCap size={18} className="text-blue-300" />
                </div>
                <div className="text-left">
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">Education</h3>
                  <p className="text-gray-500 text-xs sm:text-sm">Academic credentials and foundation</p>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-2 bottom-2 w-px bg-white/10 md:-translate-x-1/2" />

                <div className="space-y-12 sm:space-y-14">
                  {education.map((exp, index) => (
                    <motion.div
                      key={exp.id || index}
                      initial={{ opacity: 0, y: 28 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      viewport={{ once: true, amount: 0.2 }}
                      className="relative grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10 lg:gap-14"
                    >
                      <div
                        className={`absolute left-4 md:left-1/2 top-3 md:top-4 w-3 h-3 rounded-full -translate-x-1/2 z-10 ${
                          index === 0
                            ? 'bg-blue-400 ring-[6px] ring-blue-400/20 shadow-[0_0_18px_rgba(96,165,250,0.4)]'
                            : 'bg-white ring-[3px] ring-gray-800'
                        }`}
                      />

                      <div className="pl-10 md:pl-0 md:pr-8 lg:pr-12 md:text-right">
                        <span className={surfacePill}>
                          {exp.duration}
                        </span>
                        <h4 className="text-xl sm:text-2xl font-bold text-white leading-snug">
                          {exp.title}
                        </h4>
                        <p className="mt-1.5 text-blue-400 font-medium text-sm sm:text-base">
                          {exp.company}
                        </p>
                        {exp.location && (
                          <p className="mt-1 text-gray-500 text-xs sm:text-sm flex items-center gap-1.5 md:justify-end">
                            <MapPin size={12} className="text-gray-500 shrink-0" />
                            {exp.location}
                          </p>
                        )}
                      </div>

                      <div className="pl-10 md:pl-8 lg:pl-12">
                        {exp.description && (
                          <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-4">
                            {exp.description}
                          </p>
                        )}

                        {exp.skills?.length > 0 && (
                          <div>
                            <p className="text-xs uppercase tracking-wider text-gray-500 mb-2.5 font-medium">
                              Skills
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {exp.skills.map((skill) => (
                                <span
                                  key={skill}
                                  className={surfaceTag}
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section id="projects" className={`flex min-h-screen items-center border-y border-white/5 py-14 sm:py-16 lg:py-20 ${pagePad}`}>
          <div className={pageWidth}>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              viewport={{ once: true, amount: 0.2 }}
              className="mb-10 text-center sm:mb-12"
            >
              <span className={surfaceEyebrow}>
                Selected Work
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-3 sm:mb-4">
                Featured <span className="text-cyan-400">Projects</span>
              </h2>
              <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
                Products and tools I've analyzed, designed, and shipped end to end.
              </p>
            </motion.div>

            <div className="relative">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                {paginatedProjects.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.35, delay: index * 0.05 }}
                    viewport={{ once: true, amount: 0.2 }}
                    onClick={() => handleProjectClick(project)}
                    className={`group flex flex-col h-full ${surfaceCardHover} overflow-hidden cursor-pointer`}
                  >
                    <div className="flex flex-col flex-1 p-6 sm:p-7">
                      <div className="text-xs font-medium uppercase tracking-wide text-cyan-400 mb-2">
                        {project.category}
                      </div>

                      <h3
                        className="font-display text-lg sm:text-xl font-semibold text-white mb-3 leading-snug line-clamp-2 group-hover:text-cyan-300 transition-colors"
                        title={project.title}
                      >
                        {project.title}
                      </h3>

                      <p
                        className="text-gray-300 leading-relaxed text-sm sm:text-base line-clamp-3 mb-5"
                        title={project.description}
                      >
                        {project.description}
                      </p>
                      <p className="text-xs text-gray-500 mb-4 group-hover:text-cyan-400/80 transition-colors">
                        View details →
                      </p>

                      <div className="mt-auto">
                        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2.5">
                          Tech stack
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {project.tech.slice(0, 4).map((tech) => (
                            <span key={tech} className={surfaceTag}>
                              {tech}
                            </span>
                          ))}
                          {project.tech.length > 4 && (
                            <span className={surfaceTag} title={project.tech.slice(4).join(', ')}>
                              +{project.tech.length - 4}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {(project.github || (project.demo && project.demo !== '#')) && (
                    <div className="flex items-center gap-2 sm:gap-3 h-[52px] px-6 sm:px-7 border-t border-white/10 bg-black/20 shrink-0">
                      {project.github && (
                        <a
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProjectGithub(project);
                          }}
                          className="inline-flex items-center space-x-2 text-gray-300 hover:text-cyan-400 transition-colors py-1.5 px-2.5 rounded-lg hover:bg-white/5 text-sm"
                        >
                          <GitHubIcon size={15} />
                          <span>Code</span>
                        </a>
                      )}
                      {project.demo && project.demo !== "#" && (
                        <a
                          href={project.demo}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProjectDemo(project);
                          }}
                          className="inline-flex items-center space-x-2 text-gray-300 hover:text-cyan-400 transition-colors py-1.5 px-2.5 rounded-lg hover:bg-white/5 text-sm"
                        >
                          <ExternalLink size={15} />
                          <span>Live Demo</span>
                        </a>
                      )}
                    </div>
                    )}
                  </motion.div>
                ))}
              </div>

              {totalProjectPages > 1 && (
                <>
                  <div className="flex items-center justify-between mt-6 sm:mt-8">
                    <button
                      onClick={() => setCurrentProjectPage(Math.max(0, currentProjectPage - 1))}
                      disabled={currentProjectPage === 0}
                      className={currentProjectPage === 0 ? btnPagerDisabled : btnPager}
                    >
                      <ChevronLeft size={16} className="sm:w-4 sm:h-4" />
                      <span>Previous</span>
                    </button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: totalProjectPages }).map((_, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setCurrentProjectPage(index)}
                          className="flex h-11 w-11 items-center justify-center"
                          aria-label={`Go to projects page ${index + 1}`}
                        >
                          <span
                            className={`block h-2.5 w-2.5 rounded-full transition-all sm:h-3 sm:w-3 ${
                              index === currentProjectPage
                                ? 'scale-125 bg-cyan-400'
                                : 'bg-gray-600 hover:bg-gray-500'
                            }`}
                          />
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => setCurrentProjectPage(Math.min(totalProjectPages - 1, currentProjectPage + 1))}
                      disabled={currentProjectPage >= totalProjectPages - 1}
                      className={currentProjectPage >= totalProjectPages - 1 ? btnPagerDisabled : btnPager}
                    >
                      <span>Next</span>
                      <ChevronRight size={16} className="sm:w-4 sm:h-4" />
                    </button>
                  </div>

                  <div className="text-center mt-3 sm:mt-4">
                    <p className="text-gray-400 text-xs sm:text-sm">
                      Showing {currentProjectPage * projectsPerPage + 1}-{Math.min((currentProjectPage + 1) * projectsPerPage, projects.length)} of {projects.length} projects
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Skills Section */}
        <section id="skills" className={`flex min-h-screen items-center border-b border-white/5 py-14 sm:py-16 lg:py-20 ${pagePad}`}>
          <div className={pageWidth}>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              viewport={{ once: true, amount: 0.2 }}
              className="mb-10 text-center sm:mb-12"
            >
              <span className={surfaceEyebrow}>
                Toolkit
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-3 sm:mb-4">
                Skills & <span className="text-cyan-400">Technologies</span>
              </h2>
              <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
                The stack I use to analyze problems and build solutions.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {Object.entries(skills).map(([category, skillData], index) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true, amount: 0.2 }}
                  className={`${surfaceCardHover} p-4 sm:p-6`}
                >
                  <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4 flex items-center space-x-2">
                    <Zap size={18} className="text-cyan-400 sm:w-5 sm:h-5" />
                    <span>{category}</span>
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {skillData.items.map(skill => (
                      <span key={skill} className={surfaceTagSoft}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Certificates Section */}
        <section id="certificates" className={`flex min-h-screen items-center border-y border-white/5 py-14 sm:py-16 lg:py-20 ${pagePad}`}>
          <div className={pageWidth}>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              viewport={{ once: true, amount: 0.2 }}
              className="mb-10 text-center sm:mb-12"
            >
              <span className={surfaceEyebrow}>
                Credentials
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-white mb-3 sm:mb-4">
                Certificates & <span className="text-cyan-400">Credentials</span>
              </h2>
              <p className="text-gray-400 text-sm sm:text-base max-w-3xl mx-auto">
                Certifications across analysis, development, and data.
              </p>
            </motion.div>

            {/* Certificates Display */}
            <div className="relative">
              {/* Certificates Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 min-h-[400px]">
                {certificates
                  .slice(currentCertPage * 6, (currentCertPage + 1) * 6)
                  .map((cert, index) => (
                    <motion.div
                      key={cert.credentialId}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.08 }}
                      viewport={{ once: true, amount: 0.2 }}
                      className={`${surfaceCardHover} p-4 sm:p-6 ${
                        cert.featured ? 'ring-1 ring-cyan-400/40' : ''
                      }`}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3 sm:mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 border border-white/10 bg-black/25 rounded-lg flex items-center justify-center flex-shrink-0">
                            <GraduationCap size={16} className="text-cyan-400 sm:w-5 sm:h-5" />
                          </div>
                          {cert.featured && (
                            <div className="bg-yellow-400 text-gray-900 px-2 py-1 rounded-full text-xs font-bold">
                              FEATURED
                            </div>
                          )}
                        </div>
                        <div className="text-emerald-400">
                          <CheckCircle size={16} className="sm:w-4 sm:h-4" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="space-y-2 sm:space-y-3">
                        <h3 className="font-display text-base sm:text-lg font-semibold text-white leading-tight">{cert.title}</h3>
                        <p className="text-cyan-400 font-semibold text-sm">{cert.issuer}</p>
                        
                        <div className="flex items-center text-xs sm:text-sm text-gray-400">
                          <Calendar size={12} className="mr-2 sm:w-3.5 sm:h-3.5" />
                          <span>{cert.date}</span>
                        </div>

                        {/* Skills */}
                        <div>
                          <div className="flex flex-wrap gap-1 mb-2 sm:mb-3">
                            {cert.skills.slice(0, 2).map(skill => (
                              <span key={skill} className={surfaceChip}>
                                {skill}
                              </span>
                            ))}
                            {cert.skills.length > 2 && (
                              <span className="text-gray-400 text-xs px-2 py-1">
                                +{cert.skills.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Credential ID */}
                        <div>
                          <p className="text-gray-400 text-xs mb-1">Credential ID:</p>
                          <p className="text-gray-300 text-xs font-mono bg-black/30 border border-white/10 px-2 py-1 rounded truncate">
                            {cert.credentialId}
                          </p>
                        </div>

                        {/* Verify Link */}
                        <a 
                          href={cert.verifyUrl} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="inline-flex items-center space-x-1 sm:space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors duration-200 text-xs sm:text-sm font-semibold mt-2 sm:mt-3"
                        >
                          <ExternalLink size={12} className="sm:w-3.5 sm:h-3.5" />
                          <span>Verify Certificate</span>
                        </a>
                      </div>
                    </motion.div>
                  ))}
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center justify-between mt-6 sm:mt-8">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentCertPage(Math.max(0, currentCertPage - 1))}
                  disabled={currentCertPage === 0}
                  className={currentCertPage === 0 ? btnPagerDisabled : btnPager}
                >
                  <ChevronLeft size={16} className="sm:w-4 sm:h-4" />
                  <span>Previous</span>
                </button>

                {/* Page Indicators */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.ceil(certificates.length / 6) }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setCurrentCertPage(index)}
                      className="flex h-11 w-11 items-center justify-center"
                      aria-label={`Go to certificates page ${index + 1}`}
                    >
                      <span
                        className={`block h-2.5 w-2.5 rounded-full transition-all sm:h-3 sm:w-3 ${
                          index === currentCertPage
                            ? 'scale-125 bg-cyan-400'
                            : 'bg-gray-600 hover:bg-gray-500'
                        }`}
                      />
                    </button>
                  ))}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => setCurrentCertPage(Math.min(Math.ceil(certificates.length / 6) - 1, currentCertPage + 1))}
                  disabled={currentCertPage >= Math.ceil(certificates.length / 6) - 1}
                  className={currentCertPage >= Math.ceil(certificates.length / 6) - 1 ? btnPagerDisabled : btnPager}
                >
                  <span>Next</span>
                  <ChevronRight size={16} className="sm:w-4 sm:h-4" />
                </button>
              </div>

              {/* Progress Indicator */}
              <div className="text-center mt-3 sm:mt-4">
                <p className="text-gray-400 text-xs sm:text-sm">
                  Showing {currentCertPage * 6 + 1}-{Math.min((currentCertPage + 1) * 6, certificates.length)} of {certificates.length} certificates
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className={`relative flex min-h-screen items-center border-t border-white/5 py-14 sm:py-16 lg:py-20 ${pagePad}`}>
          <div className={pageWidth}>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              viewport={{ once: true, amount: 0.2 }}
              className="mb-10 text-center sm:mb-12"
            >
              <span className={surfaceEyebrow}>
                Contact
              </span>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-white mb-3 sm:mb-4">
                Let's Have a <span className="text-cyan-400">Chat</span>
              </h2>
              <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto">
                Leave your email and I will get back to you within 24-48 hours
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-stretch">
              {/* Left Side - Contact info */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                viewport={{ once: true, amount: 0.2 }}
                className={`relative overflow-hidden ${surfaceCard} flex h-full flex-col p-6 sm:p-8`}
              >
                <div className="relative z-10 flex flex-1 flex-col">
                  <div>
                    <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-gray-500">Contact</p>
                    <h3 className="font-display text-2xl font-semibold text-white sm:text-3xl">Get in Touch</h3>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-gray-400 sm:text-base">
                      Open to roles, collaborations, and product problems worth solving.
                    </p>
                  </div>

                  <div className="mt-8 space-y-5 sm:mt-10 sm:space-y-6">
                    <div className="rounded-xl border border-white/10 bg-black/25 p-4">
                      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-cyan-300">Email</h4>
                      <a 
                        href={`mailto:${personalInfo.email}`}
                        onClick={() => handleSocialClick('Email', personalInfo.email)}
                        className="break-all text-base text-white transition-colors hover:text-cyan-300 sm:text-lg"
                      >
                        {personalInfo.email}
                      </a>
                    </div>
                    
                    <div className="rounded-xl border border-white/10 bg-black/25 p-4">
                      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-cyan-300">Working hours</h4>
                      <p className="text-base text-white sm:text-lg">10:00 AM - 6:00 PM EST</p>
                      <p className="mt-1 text-sm text-gray-500">Usually replies within 24-48 hours</p>
                    </div>
                    
                    <div className="rounded-xl border border-white/10 bg-black/25 p-4">
                      <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-cyan-300">Location</h4>
                      <p className="text-base text-white sm:text-lg">{personalInfo.location}</p>
                    </div>
                  </div>

                  <div className="mt-auto pt-8">
                    <p className="mb-3 text-xs uppercase tracking-[0.16em] text-gray-500">Connect</p>
                    <div className="flex space-x-3 sm:space-x-4">
                      <motion.a
                        href={socialLinks.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleSocialClick('GitHub', socialLinks.github)}
                        className={surfaceIconBtn}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.97 }}
                        aria-label="GitHub"
                      >
                        <GitHubIcon size={20} className="sm:w-6 sm:h-6" />
                      </motion.a>
                      
                      <motion.a
                        href={socialLinks.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleSocialClick('LinkedIn', socialLinks.linkedin)}
                        className={surfaceIconBtn}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.97 }}
                        aria-label="LinkedIn"
                      >
                        <LinkedInIcon size={20} className="sm:w-6 sm:h-6" />
                      </motion.a>
                      
                      <motion.a
                        href={socialLinks.x_twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleSocialClick('X Twitter', socialLinks.x_twitter)}
                        className={surfaceIconBtn}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.97 }}
                        aria-label="X"
                      >
                        <XIcon size={20} className="sm:w-6 sm:h-6" />
                      </motion.a>
                      
                      <motion.a
                        href={`mailto:${personalInfo.email}`}
                        onClick={() => handleSocialClick('Email', personalInfo.email)}
                        className={surfaceIconBtn}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.97 }}
                        aria-label="Email"
                      >
                        <Mail size={20} className="sm:w-6 sm:h-6" />
                      </motion.a>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Right Side - Contact Form */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.08 }}
                viewport={{ once: true, amount: 0.2 }}
                className={`${surfaceCard} flex h-full flex-col p-6 sm:p-8`}
              >
                {/* Success Message */}
                {formStatus.isSuccess && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-green-300 font-semibold text-sm sm:text-base">Message sent successfully!</p>
                        <p className="text-green-400 text-xs sm:text-sm">I'll get back to you within 24-48 hours.</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Error Message */}
                {formStatus.isError && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-red-300 font-semibold text-sm sm:text-base">Failed to send message</p>
                        <p className="text-red-400 text-xs sm:text-sm">{formStatus.errorMessage}</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                <form onSubmit={handleFormSubmit} className="space-y-4 sm:space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-white text-base sm:text-lg font-medium mb-2 sm:mb-3">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your Full Name"
                      required
                      disabled={formStatus.isSubmitting}
                      className={surfaceInput}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-white text-base sm:text-lg font-medium mb-2 sm:mb-3">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your.email@example.com"
                      required
                      disabled={formStatus.isSubmitting}
                      className={surfaceInput}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-white text-base sm:text-lg font-medium mb-2 sm:mb-3">
                      Subject
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      disabled={formStatus.isSubmitting}
                      className={surfaceInput}
                    >
                      <option value="" className="text-gray-400">Select a subject</option>
                      <option value="project-inquiry" className="text-gray-900">Project Inquiry</option>
                      <option value="collaboration" className="text-gray-900">Collaboration Opportunity</option>
                      <option value="job-opportunity" className="text-gray-900">Job Opportunity</option>
                      <option value="freelance-work" className="text-gray-900">Freelance Work</option>
                      <option value="consultation" className="text-gray-900">Technical Consultation</option>
                      <option value="general" className="text-gray-900">General Inquiry</option>
                      <option value="other" className="text-gray-900">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-white text-base sm:text-lg font-medium mb-2 sm:mb-3">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      placeholder="Tell me about the role, project, or problem you're working on..."
                      required
                      disabled={formStatus.isSubmitting}
                      className={`${surfaceInput} resize-none`}
                    />
                  </div>
                  
                  <motion.button
                    type="submit"
                    disabled={formStatus.isSubmitting}
                    className={`w-full ${btnPrimary} disabled:bg-gray-600 disabled:text-gray-300 disabled:hover:bg-gray-600 disabled:cursor-not-allowed py-3 sm:py-4`}
                    whileHover={!formStatus.isSubmitting ? { scale: 1.01 } : {}}
                    whileTap={!formStatus.isSubmitting ? { scale: 0.99 } : {}}
                  >
                    {formStatus.isSubmitting ? (
                      <>
                        <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Mail size={18} className="sm:w-5 sm:h-5" />
                        <span>Get in Touch</span>
                      </>
                    )}
                  </motion.button>
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className={`border-t border-white/10 bg-black/30 ${pagePad} py-10 sm:py-12`}>
        <div className={pageWidth}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 lg:gap-12 md:items-stretch">
            {/* Brand */}
            <div className="flex flex-col h-full">
              <h4 className="font-display text-xs uppercase tracking-[0.16em] text-gray-500 mb-4">
                About
              </h4>
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-9 h-9 border border-white/20 rounded-full flex items-center justify-center shrink-0">
                  <span className="font-display text-white font-semibold text-sm tracking-tight">HA</span>
                </div>
                <div>
                  <h3 className="font-display text-base sm:text-lg font-semibold text-white leading-tight">
                    {personalInfo.name}
                  </h3>
                  <p className="text-gray-500 text-xs">Technical Analyst & Developer</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Turning analysis into shipped tools and products.
              </p>
              <div className="flex space-x-2 mt-auto pt-6">
                <motion.a
                  href={socialLinks.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleSocialClick('GitHub', socialLinks.github)}
                  className="w-9 h-9 border border-white/10 bg-black/25 rounded-lg flex items-center justify-center text-gray-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-all"
                  whileHover={{ scale: 1.03 }}
                  aria-label="GitHub"
                >
                  <GitHubIcon size={15} />
                </motion.a>
                <motion.a
                  href={socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleSocialClick('LinkedIn', socialLinks.linkedin)}
                  className="w-9 h-9 border border-white/10 bg-black/25 rounded-lg flex items-center justify-center text-gray-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-all"
                  whileHover={{ scale: 1.03 }}
                  aria-label="LinkedIn"
                >
                  <LinkedInIcon size={15} />
                </motion.a>
                <motion.a
                  href={socialLinks.x_twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleSocialClick('X Twitter', socialLinks.x_twitter)}
                  className="w-9 h-9 border border-white/10 bg-black/25 rounded-lg flex items-center justify-center text-gray-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-all"
                  whileHover={{ scale: 1.03 }}
                  aria-label="X"
                >
                  <XIcon size={14} />
                </motion.a>
                <motion.a
                  href={`mailto:${personalInfo.email}`}
                  onClick={() => handleSocialClick('Email', personalInfo.email)}
                  className="w-9 h-9 border border-white/10 bg-black/25 rounded-lg flex items-center justify-center text-gray-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-all"
                  whileHover={{ scale: 1.03 }}
                  aria-label="Email"
                >
                  <Mail size={15} />
                </motion.a>
              </div>
            </div>

            {/* Quick Links — two columns so height matches siblings */}
            <div className="flex flex-col h-full">
              <h4 className="font-display text-xs uppercase tracking-[0.16em] text-gray-500 mb-4">
                Quick Links
              </h4>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-2.5 content-start">
                {[
                  { id: 'hero', label: 'Home' },
                  { id: 'about', label: 'About Me' },
                  { id: 'experience', label: 'Experience' },
                  { id: 'projects', label: 'Projects' },
                  { id: 'skills', label: 'Skills' },
                  { id: 'certificates', label: 'Certificates' },
                  { id: 'contact', label: 'Contact' },
                ].map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(item.id)}
                      className="text-gray-300 hover:text-cyan-300 transition-colors text-sm"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact — info only; form CTA lives in the section above */}
            <div className="flex flex-col h-full">
              <h4 className="font-display text-xs uppercase tracking-[0.16em] text-gray-500 mb-4">
                Contact
              </h4>
              <ul className="space-y-2.5 text-sm text-gray-300 flex-1">
                <li className="flex items-start gap-2.5">
                  <MapPin size={14} className="text-cyan-400 mt-0.5 shrink-0" />
                  <span>{personalInfo.location}</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Mail size={14} className="text-cyan-400 mt-0.5 shrink-0" />
                  <a
                    href={`mailto:${personalInfo.email}`}
                    onClick={() => handleSocialClick('Email', personalInfo.email)}
                    className="hover:text-cyan-300 transition-colors break-all"
                  >
                    {personalInfo.email}
                  </a>
                </li>
                <li className="flex items-start gap-2.5">
                  <Globe size={14} className="text-cyan-400 mt-0.5 shrink-0" />
                  <span>Available remotely</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Clock size={14} className="text-cyan-400 mt-0.5 shrink-0" />
                  <span>24-48h response</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <Briefcase size={14} className="text-cyan-400 mt-0.5 shrink-0" />
                  <span>Open to opportunities</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-gray-500 text-xs sm:text-sm">
              © {new Date().getFullYear()} {personalInfo.name}. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-cyan-300 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-cyan-300 transition-colors">
                Terms of Service
              </Link>
              <Link to="/sitemap" className="text-gray-400 hover:text-cyan-300 transition-colors">
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </footer>
      {/* Project detail modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6"
            onClick={closeProjectDetail}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.25 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="project-detail-title"
              className={`relative z-10 w-full max-w-2xl max-h-[85vh] overflow-y-auto ${surfaceCard} p-6 sm:p-8`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-cyan-400 mb-2">
                    {selectedProject.category}
                  </p>
                  <h3
                    id="project-detail-title"
                    className="font-display text-xl sm:text-2xl font-semibold text-white leading-snug"
                  >
                    {selectedProject.title}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={closeProjectDetail}
                  className="shrink-0 p-2 rounded-lg border border-white/10 bg-black/25 text-gray-400 hover:text-white hover:border-white/20 transition-colors"
                  aria-label="Close project details"
                >
                  <X size={18} />
                </button>
              </div>

              <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-6">
                {selectedProject.description}
              </p>

              {selectedProject.highlights?.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-medium">
                    Highlights
                  </p>
                  <ul className="space-y-2.5">
                    {selectedProject.highlights.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-gray-300 text-sm leading-relaxed">
                        <span className="mt-2 h-1 w-1 rounded-full bg-cyan-400/80 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedProject.tech?.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-3 font-medium">
                    Tech stack
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.tech.map((tech) => (
                      <span key={tech} className={surfaceTag}>
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3 pt-2 border-t border-white/10">
                {selectedProject.github && (
                  <a
                    href={selectedProject.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleProjectGithub(selectedProject)}
                    className={btnSecondary}
                  >
                    <GitHubIcon size={16} />
                    <span>View Code</span>
                  </a>
                )}
                {selectedProject.demo && selectedProject.demo !== '#' && (
                  <a
                    href={selectedProject.demo}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleProjectDemo(selectedProject)}
                    className={btnPrimary}
                  >
                    <ExternalLink size={16} />
                    <span>Live Demo</span>
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TechPage;